import { EventEmitter, Inject, Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';

import { catchAndSurfaceError } from '@app/shared/utils/dg-error-helpers';

import { UserOutcomeDetail } from '@app/outcomes/outcomes-api';
import { InputDetails, UserPosition } from '@app/inputs/inputs-api';

import { SimpleModalComponent } from '@app/shared/components/modal/simple-modal/simple-modal.component';
import { isOutcomeTile } from '@app/profile/components/profile-collection/services/profile-collection-helpers';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';

import { FeedbackService } from '@app/feedback/services/feedback.service';
import { UserOutcomeModalService } from '@app/outcomes/services/user-outcome-modal.service';
import { NotifierService } from '@app/shared/services/notifier.service';
import { TranslateService } from '@ngx-translate/core';
import { InputsService } from '@app/inputs/services/inputs.service';
import { AuthService } from '@app/shared/services/auth.service';
import { NgxHttpClient } from '@app/shared/ngx-http-client';
import { TrackerService } from '@app/shared/services/tracker.service';
import { ModalService } from '@app/shared/services/modal.service';
import { Profile } from '@app/profile/profile';
import { InputModalDispatcherService } from '@app/user-content/user-input/services/input-modal-dispatcher.service';
import { FocusStackService } from '@app/shared/services/focus-stack.service';

import { Router } from '@angular/router';
import { A11yService, ClipboardService } from '@dg/shared-services';
import { WindowToken } from '@app/shared/window.token';

export interface DeleteModalPayload {
  contentCategory: string;
  contentType: string;
  itemId: number;
  userItemId: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProfileOverviewActionsServiceV2 {
  public i18n = this.translate.instant([
    'Core_GeneralErrorMessage',
    'Core_Edit',
    'Core_YesSure',
    'Core_Delete',
    'Core_ReportAProblem',
    'Core_Select',
    'Pathways_PathwayAccessError',
    'UserProfileSvc_ProblemDeletingCollectionItem',
    'UserProfileSvc_ProblemUpdatingCollectionItem',
    'Core_Settings',
    'ProfileCtrl_ShareProfileSuccess',
    'ProfileCtrl_ShareProfile',
  ]);

  public inputSelectedSubject$ = new Subject();
  public outcomeSelectedSubject$ = new Subject();

  constructor(
    private translate: TranslateService,
    private notifierService: NotifierService,
    private userOutcomeModalService: UserOutcomeModalService,
    private modalService: ModalService,
    private feedbackService: FeedbackService,
    private inputsService: InputsService,
    private authService: AuthService,
    private http: NgxHttpClient,
    private trackerService: TrackerService,
    private genericModalService: InputModalDispatcherService,
    private focusStackService: FocusStackService,
    private router: Router,
    private clipboardService: ClipboardService,
    private a11yService: A11yService,
    @Inject(WindowToken) private windowRef: Window
  ) {}

  // This can be removed when the new user-info component is released
  public getUserInfoMenu(owner: Profile): MenuViewModel[] {
    return [
      {
        title: this.i18n.Core_ReportAProblem,
        defaultAction: (_, popoverTrigger) => {
          this.feedbackService
            .showReportProblemModal(
              {
                itemType: this.feedbackService.feedbackItemType.Profile,
                itemId: owner.userProfileKey,
              },
              popoverTrigger.nativeElement
            )
            .subscribe();
        },
      },
    ];
  }

  /**
   * Get the config for the meatball menu on the UserInfoComponent.
   *
   * @param owner - The user info component profile object.
   */
  public getNonOwnerUserInfoMenu(owner: Profile): MenuViewModel[] {
    return [
      {
        title: this.i18n.ProfileCtrl_ShareProfile,
        defaultAction: (_, popoverTrigger) => {
          this.copyProfileUrl(owner, true);
        },
      },
      {
        title: this.i18n.Core_ReportAProblem,
        defaultAction: (_, popoverTrigger) => {
          this.feedbackService
            .showReportProblemModal(
              {
                itemType: this.feedbackService.feedbackItemType.Profile,
                itemId: owner.userProfileKey,
              },
              popoverTrigger.nativeElement
            )
            .subscribe();
        },
      },
    ];
  }

  public getOwnerUserInfoMenu(
    owner: Profile,
    isMobile: boolean
  ): MenuViewModel[] {
    return [
      {
        title: this.i18n.Core_Settings,
        defaultAction: (_, popoverTrigger) => {
          this.router.navigateByUrl(`/profile/${owner.vanityUrl}/settings`);
        },
        isHidden: () => !isMobile,
      },
      {
        title: this.i18n.ProfileCtrl_ShareProfile,
        defaultAction: (_, popoverTrigger) => {
          this.copyProfileUrl(owner, true);
        },
      },
    ];
  }

  public copyProfileUrl(owner: Profile, showSuccessNotification = false): void {
    const ownerUrl = owner.vanityUrl;
    let profileUrl = this.windowRef.location.origin;
    profileUrl += `/profile/${ownerUrl}`;
    this.clipboardService.copyToClipboard(profileUrl);
    this.a11yService.announcePolite(this.i18n.ProfileCtrl_ShareProfile);
    if (showSuccessNotification) {
      this.notifierService.showSuccess(
        this.i18n.ProfileCtrl_ShareProfileSuccess
      );
    }
  }

  /**
   * Show the edit modal for a userOutcome - award, degree, accomplishment, certificate
   * @param event - click event from the menu
   * @param userOutcome - the award, accomplishment, degree or certificate to edit
   * @param eventEmitter - eventEmitter to emit when the userOutcome has been successfully edited
   */
  // Used in UserOutcomeActions only
  public editUserOutcome(
    event: Event,
    userOutcome: UserOutcomeDetail,
    eventEmitter: EventEmitter<UserOutcomeDetail | UserPosition>
  ): void {
    this.userOutcomeModalService
      .edit({
        outcomeType: userOutcome.contentTypeId,
        userOutcomeId: userOutcome.userOutcomeId,
        sourceTarget: event.target,
      })
      .subscribe(
        () => {
          eventEmitter.emit(userOutcome);
          this.notifierService.showSuccess(
            this.translate.instant(
              'ProfileCtrl_EditCollectionItemNotifierFormat',
              {
                itemTitle:
                  userOutcome.contentType === 'Badge' && !userOutcome?.title
                    ? 'Badge'
                    : userOutcome.title,
              }
            )
          );
        },
        (e) => {
          if (e) {
            console.error(e);
          }
        }
      );
  }

  /**
   * Show the delete modal for a userOutcome / experience
   * @param headerText - The header text for the modal
   * @param bodyText - The body text for the modal
   * @param payload - The full user outcome or experience to delete
   * @param title - The item title
   * @param eventEmitter - eventEmitter to emit when the item has been successfully deleted
   */
  // used in UserOutcomeActions and UserExperienceActions
  public showDeleteModal(
    headerText: string,
    bodyText: string,
    payload: DeleteModalPayload,
    title: string,
    eventEmitter?: EventEmitter<UserOutcomeDetail | UserPosition>
  ): void {
    // use setTimeout to prevent modal focus being lost
    setTimeout(() =>
      this.modalService
        .show<void>(SimpleModalComponent, {
          inputs: {
            canCancel: true,
            bodyClasses: 'center-text',
            bodyText,
            headerText,
            submitButtonText: this.i18n.Core_YesSure,
          },
        })
        .subscribe(() => {
          this.deleteCollectionItem(payload).subscribe(() => {
            eventEmitter?.emit();
            this.inputsService.notifyInputModified(payload.contentType);
            this.notifierService.showSuccess(
              this.translate.instant(
                'ProfileCtrl_DeleteCollectionItemNotifierFormat',
                {
                  itemTitle: title,
                }
              )
            );
          });
        })
    );
  }

  // used in UserOutcomeTile only
  public deleteCollectionItem(item): Observable<void> {
    return this.http.post('/userprofile/deletecollectionitem', item).pipe(
      map(() => {
        this.trackerService.trackEventData({
          action: 'User Item Deleted',
          category: item.contentType,
        });
      }),
      catchAndSurfaceError(
        this.i18n.UserProfileSvc_ProblemDeletingCollectionItem
      )
    );
  }

  // used by ProfilePathways only
  public getProfilePathways(userProfileKey: number): Observable<any> {
    return this.http
      .get('/pathways/getpathwaysforuserprofile', {
        params: {
          requestedUserKey: userProfileKey,
          useResourceImages: true,
        },
      })
      .pipe(catchAndSurfaceError(this.i18n.Pathways_PathwayAccessError));
  }

  /**
   * Show the report a problem modal for an item
   * @param event - The click event from the menu
   * @param itemId - The id of the item to report
   * @param itemType - The type of item being reported
   * @param refocusElement
   */
  // used by UserExperienceActions, SkillSignalMoreButton, UserOutcomeActions
  public showReportModal(
    event: Event,
    itemId: number,
    itemType: string,
    refocusElement: HTMLElement
  ): Observable<any> {
    return this.feedbackService.showReportProblemModal(
      {
        itemType: this.feedbackService.getFeedbackItemTypeFromResource(
          itemType
        ),
        itemId: itemId,
      },
      refocusElement
    );
  }

  public selectOutcome(item: any) {
    this.outcomeSelectedSubject$.next(item);
  }

  public selectInput(item: any) {
    this.inputSelectedSubject$.next(item);
  }

  // Used by ProfileLearning, ProfileCollection
  public modifyInputMenuOptions = (
    isOwner: boolean,
    showSelect: boolean,
    content,
    defaultConfig
  ) => {
    const authUser = this.authService.authUser;
    const shareOption = defaultConfig.find((c) => c.title === 'Share');

    if (shareOption) {
      const hideShareDefault = shareOption.isHidden();
      shareOption.isHidden = () =>
        hideShareDefault || content.model.inputType === 'Position';
    }

    return [
      {
        title: this.i18n.Core_Select,
        isHidden: () => !authUser || !isOwner || !showSelect,
        defaultAction: (_, popoverTrigger) => {
          if (isOutcomeTile(content.contentType)) {
            this.selectOutcome(content);
          } else {
            this.selectInput(content);
          }
        },
      },
      {
        title: this.i18n.Core_Edit,
        isHidden: () =>
          !authUser ||
          !isOwner ||
          content.resourceType === 'Task' ||
          content.resourceType === 'Article' ||
          content.resourceType === 'Event' ||
          content.resourceType === 'Book',
        defaultAction: (_, popoverTrigger) => {
          this.editLearningContentGenericModal(content);
        },
      },
      {
        title: this.i18n.Core_Delete,
        isHidden: () => !authUser || !isOwner,
        defaultAction: (event, popoverTrigger) => {
          this.deleteLearningContent(content, popoverTrigger);
        },
        preventRefocus: true,
      },
      ...defaultConfig,
    ];
  };

  private editLearningContentGenericModal(content): void {
    this.genericModalService
      .edit({
        inputId: content.model.inputId,
        userInputId: content.model.userInputId,
        inputType: content.model.inputType,
        initialModel: {
          inputId: content.model.inputId,
          userInputId: content.model.userInputId,
          inputType: content.model.inputType,
          externalCompletionOnly: content.model.externalCompletionOnly,
        },
      })
      .subscribe({
        next: (item: any) => {
          // dismissed
          if (!item) {
            return;
          }
          this.inputsService.notifyInputModified(content.model.inputType);
          // No need for notifierService notification here. The new generic Input modal system handles that internally.
        },
        error: (e) => {
          if (e) {
            console.error(e);
          }
        },
      });
  }

  private deleteLearningContent(content, popoverTrigger): void {
    this.focusStackService.push(popoverTrigger?.nativeElement);
    const inputDetails = content.model as InputDetails;
    const headerText = this.translate.instant(
      'confirmModal_Delete' + inputDetails.inputType + 'Title'
    );
    const bodyText = this.translate.instant(
      'confirmModal_Delete' + inputDetails.inputType + 'Body'
    );

    const payload: DeleteModalPayload = {
      contentCategory: 'Input',
      contentType: inputDetails.resourceType,
      itemId: inputDetails.inputId,
      userItemId: inputDetails.userInputId,
    };
    this.showDeleteModal(headerText, bodyText, payload, content.model.title);
  }
}
